import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { css } from '@emotion/react';
import Link from 'next/link';

// Dynamically import MUI components
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
const IconButton= dynamic(() => import('@backpack/forms/IconButton'))
const Typography = dynamic(() => import('@mui/material/Typography'));
const Drawer = dynamic(() => import('@mui/material/Drawer'));
const MenuItem = dynamic(() => import('@mui/material/MenuItem'));

const MenuIcon = dynamic(() =>
  import('feather-icons-react').then((mod) => mod.Menu),
  { ssr: false }
);

const AirplayIcon = dynamic(() =>
  import('feather-icons-react').then((mod) => mod.Airplay),
  { ssr: false }
);
// Emotion styles using css prop
const headerStyles = theme => css`
  background-color: ${theme.palette.primary.main};
  margin: 0;
  width: 100%;

  .appbar {
    background-color: ${theme.palette.primary.main};
    width: 100%;
    padding: 0;
    box-sizing: border-box;
  }
`;

const toolbarStyles = css`
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 1200px;
  padding: 0 4px;
  margin: 0 auto;
  box-sizing: border-box;
`;

const titleStyles = css`
  margin: 0;
  padding: 0;
  text-transform: uppercase;
  flex: 1;
  color: white;
  text-decoration: none;
`;

const linkStyles = css`
  margin: 0;
  padding: 0;
  text-transform: uppercase;
  flex: 1;
  color: white;
  text-decoration: none;
  @media (max-width: 600px) {
    color: black;
  }
`;

const buttonStyles = css`
  color: white;
  text-decoration: none;
  text-transform: uppercase;
  margin: 0 10px;
  padding: 0;
  flex-shrink: 0;
`;

const mobileTypStyles = css`
  text-transform: uppercase;
  @media (max-width: 600px) {
    color: black;
  }
`;

const Navigation = ({ author, menu }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const checkMobileView = () => {
      setIsMobile(window.innerWidth < 600);
    };

    // Initial check
    checkMobileView();

    // Add resize listener
    window.addEventListener('resize', checkMobileView);

    // Cleanup listener
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  const goToHomePage = () => {
    router.push('/');
  };

  const renderLogo = (clickEvent) => (
    <IconButton role="button" aria-label="menu" tabIndex={0} onClick={clickEvent} onKeyDown={(e) => e.key === 'Enter' && clickEvent()}>
      {isMobile ? <MenuIcon /> : <AirplayIcon />}
    </IconButton>
  );

  const renderMenuButtons = () => (
    menu.map(({ name, link }) => (
      <Link href={link} key={name} css={buttonStyles}>
        <Typography variant="h6" noWrap>
          {name}
        </Typography>
      </Link>
    ))
  );

  const renderDrawerChoices = () => (
    menu.map(({ name, link }) => (
      <Link
        href={link}
        key={name}
        css={linkStyles}
        onClick={() => setDrawerOpen(false)}
      >
        <MenuItem>
          <Typography css={mobileTypStyles} variant="h3" align="center" noWrap>
            {name}
          </Typography>
        </MenuItem>
      </Link>
    ))
  );

  const displayDesktop = () => (
    <Toolbar css={toolbarStyles}>
      {renderLogo(goToHomePage)}
      <Link href="/" css={linkStyles}>
        <Typography variant="h6" noWrap>
          {author}
        </Typography>
      </Link>
      {renderMenuButtons()}
    </Toolbar>
  );

  const displayMobile = () => (
    <Toolbar css={toolbarStyles}>
      {renderLogo(() => setDrawerOpen(true))}
      <Drawer
        anchor="top"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <nav>{renderDrawerChoices()}</nav>
      </Drawer>

      <Link href="/" css={titleStyles}>
        <Typography variant="h6" noWrap>
          {author}
        </Typography>
      </Link>
    </Toolbar>
  );

  return (
    <header css={theme => headerStyles(theme)}>
      <AppBar position="static" elevation={1} component="nav">
        {isMobile ? displayMobile() : displayDesktop()}
      </AppBar>
    </header>
  );
};

export default Navigation;

